import { Alert, internalLinkToast } from '@frontend/components/utils';
import {
  useCanvasCollect,
  useCanvasPublish,
  useProjectPermission,
} from '@frontend/editor/data-access';
import { PublishError } from '@frontend/editor/interface';
import {
  useGetCommentReply,
  useGetCommentReplyConditionV2,
  usePostCommentReplyActiveV2,
  useProjectLinked,
} from '@frontend/sorghum/data-access';
import { UICtx } from '@frontend/sorghum/external-providers';
import {
  CommentReplyPostType,
  FlowChart,
  ModalTypesEnum,
  PublishStatus,
} from '@frontend/sorghum/interface';
import { get, isEmpty } from 'lodash';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';

interface UseNodeInstagramCommentReplyBlockOptions {
  /**
   * 發佈前驗證失敗時回報第一個出錯元素（含 elementID / type / i18n message）。
   * 桌機畫布已透過 setPublishErrors 標紅出錯元素；手機線性表單沒有畫布，
   * 需要這個 callback 才能捲動定位到出錯的模組卡片。
   */
  onValidationError?: (error: PublishError) => void;
}

export const useNodeInstagramCommentReplyBlock = (
  commentAutoReplyID: string,
  options?: UseNodeInstagramCommentReplyBlockOptions,
) => {
  const [t] = useTranslation('instagram');
  const { openModal } = useContext(UICtx);
  const { isTemplateEditor } = useProjectPermission();
  const { data: linked } = useProjectLinked();
  const { mutate: activeCommentV2, isLoading: isLoadingV2 } = usePostCommentReplyActiveV2();
  const onValidationError = options?.onValidationError;

  const { data } = useGetCommentReply(commentAutoReplyID || '');
  const conditionID = get(data, 'replyCondition.data.0.id', '');
  const { data: conditionData } = useGetCommentReplyConditionV2(conditionID);

  const { getErrors } = useCanvasCollect();
  const { getPublishData } = useCanvasPublish();
  const { isViewer } = useProjectPermission();

  const handleSwitchCommentReply = useCallback(
    (active: boolean) => {
      // 檢查連線
      if (!linked?.ig?.id && !isViewer && !isTemplateEditor) {
        openModal(ModalTypesEnum.CONNECT_INSTAGRAM_MODAL);
        return;
      }

      // switch off
      if (!active) {
        activeCommentV2(
          {
            id: commentAutoReplyID,
            enable: false,
          },
          {
            onSuccess: () => {
              Alert.success(
                t('commentReplyDetail.alert.infoSuccess', {
                  name: data?.name,
                }),
              );
            },
            onError: () => {
              Alert.error(t('commentReplyDetail.alert.activeError'));
            },
          },
        );
        return;
      }

      // switch on

      // 檢查 schedule / selectPosts
      if (!data?.schedulePeriod?.isComplete || !data?.selectPosts?.isComplete) {
        Alert.error(t('commentReplyDetail.alert.empty'));
        return;
      }

      // 至少開啟一種回覆內容
      if (conditionData?.replyContentType === 4) {
        Alert.error(t('commentReplyDetail.alert.atLeast'));
        return;
      }

      // 檢查 private reply
      if (
        conditionData?.replyContentType === 1 ||
        conditionData?.replyContentType === 2
      ) {
        const errors = getErrors();

        if (errors.length > 0) {
          // 中止發佈並顯示可讀錯誤（含 followCheck 分支未接下一步等 canvas 驗證錯誤），
          // 同時把出錯元素回報給呼叫端做捲動/highlight 定位（手機表單用）
          Alert.error(errors[0].message);
          onValidationError?.(errors[0]);
          return;
        }
      }

      if (data.postType === CommentReplyPostType.POST) {
        // 檢查 public reply
        if (
          conditionData?.replyContentType === 1 ||
          conditionData?.replyContentType === 3
        ) {
          const emptyContentList =
            conditionData?.publicReplyContent?.filter((item) =>
              isEmpty(item),
            ) || [];
          if (
            emptyContentList.length > 0 ||
            conditionData?.publicReplyContent?.length === 0
          ) {
            Alert.error(t('commentReplyDetail.alert.empty'));
            return;
          }
        }
      }

      const publishData = getPublishData();
      // 🔴 資料損毀防線：序列化結果為空、但已存 flow 有內容（例如手機端畫布尚未 hydrate）時，
      // 中止發佈，絕不用空 chart 覆寫掉既有 flow（曾造成桌機 flow 被清空）。
      const storedChartData = conditionData?.privateReplyContent?.chart?.chart?.data ?? [];
      if (publishData.chart.data.length === 0 && storedChartData.length > 0) {
        Alert.error(t('commentReplyDetail.alert.empty'));
        return;
      }
      const chart = {
        name: conditionData?.privateReplyContent?.chart?.name,
        publishStatus: PublishStatus.PUBLISHED,
        updatedAt: new Date().toISOString(),
        chart: {
          data: publishData.chart.data,
          blockSerialNumber: publishData.chart.data.length,
        },
      } as FlowChart;

      activeCommentV2(
        {
          id: commentAutoReplyID as string,
          enable: true,
          flows: [
            {
              flowId: conditionData?.privateReplyContent?.id as string,
              blocks: publishData.blocks,
              chart,
            },
          ],
        },
        {
          onSuccess: () => {
            openModal(ModalTypesEnum.ACTIVE_COMMENT_AUTO_REPLY);
          },
          onError: (error) => {
            // 40000~40003 是設定不完整（連結/貼文/期間/條件），提示檢查設定；
            // 其他（500/504 等伺服器失敗）提示稍後再試，不要誤導客戶去改設定
            const code = (error as { code?: number })?.code;
            if (code && code >= 40000 && code <= 40003) {
              Alert.error(t('commentAutoReply.content.activeFailed'));
            } else {
              Alert.error(t('commentReplyDetail.alert.activeError'));
            }
          },
        },
      );
    },
    [
      linked?.ig?.id,
      isViewer,
      isTemplateEditor,
      data?.schedulePeriod?.isComplete,
      data?.selectPosts?.isComplete,
      data?.postType,
      data?.name,
      conditionData?.replyContentType,
      conditionData?.privateReplyContent?.chart?.name,
      conditionData?.privateReplyContent?.chart?.chart?.data,
      conditionData?.privateReplyContent?.id,
      conditionData?.publicReplyContent,
      getPublishData,
      activeCommentV2,
      commentAutoReplyID,
      openModal,
      t,
      getErrors,
      onValidationError,
    ],
  );

  const handleBlockClick = useCallback(() => {
    if (isViewer) return;
    if (data?.isActive) {
      internalLinkToast(
        t('commentReplyDetail.alert.info'),
        t('commentReplyDetail.alert.infoAction'),
        'info',

        () =>
          // 停用一律走 V2：V1 /commentReply/active 已封存不再呼叫；
          // V2 失敗會 reject，需補 onError 提示（V1 失敗會靜默假成功）
          activeCommentV2(
            {
              id: commentAutoReplyID,
              enable: false,
            },
            {
              onSuccess: () => {
                Alert.success(
                  t('commentReplyDetail.alert.infoSuccess', {
                    name: data?.name,
                  }),
                );
              },
              onError: () => {
                Alert.error(t('commentReplyDetail.alert.activeError'));
              },
            },
          ),
      );
    }
  }, [
    isViewer,
    data?.isActive,
    data?.name,
    t,
    activeCommentV2,
    commentAutoReplyID,
  ]);

  return {
    handleSwitchCommentReply,
    handleBlockClick,
    isLoading: isLoadingV2,
  };
};
